import { useQuery, useLazyQuery, gql } from "@apollo/client"
import { offsetLimitPagination } from "@apollo/client/utilities"
import * as Sentry from "@sentry/browser"
import { toId } from "../../utils"

const ACTIONS_LIMIT = 5

const ACTION_FIELDS = gql`
  fragment ActionFields on Action {
    id
    title
    content
    users {
      id
      fullName
    }
    groups {
      id
      name
    }
    uploads {
      id
      key
      fileName
      fileGroup
      fileSize
    }
    job {
      id
      title
      availableFrom
      process {
        id
      }
      location {
        id
        name
        timeZone
      }
    }
    jobStep {
      id
      step
      name
      description
    }
    resolvedAt
    resolvedBy {
      id
      fullName
      firstName
      lastName
      avatar {
        key
      }
    }
    author {
      id
      firstName
      lastName
      fullName
      avatar {
        key
      }
    }
    comments {
      id
      content
      uploads {
        id
        key
        fileName
        fileGroup
        fileSize
      }
      author {
        id
        firstName
        lastName
        avatar {
          key
        }
      }
      createdAt
    }
    createdAt
    updatedAt
  }
`

const ACTIONS_QUERY = gql`
  ${ACTION_FIELDS}
  query Actions($set: String, $offset: Int) {
    actions(set: $set, limit: ${ACTIONS_LIMIT}, offset: $offset) {
      ...ActionFields    
    } 
  }
`

const actionsQueryTypePolicies = {
  actions: offsetLimitPagination(["set"]),
}

const ACTION_CREATED_SUBSCRIPTION = gql`
  ${ACTION_FIELDS}
  subscription actionCreated {
    actionCreated {
      ...ActionFields
    }
  }
`
const ACTION_UPDATED_SUBSCRIPTION = gql`
  ${ACTION_FIELDS}
  subscription actionUpdated {
    actionUpdated {
      ...ActionFields
    }
  }
`

const ACTION_DELETED_SUBSCRIPTION = gql`
  subscription actionDeleted {
    actionDeleted
  }
`

const ACTION_COMMENT_CREATED_SUBSCRIPTION = gql`
  subscription actionCommentCreated {
    actionCommentCreated {
      id
      content
      uploads {
        id
        key
        fileName
        fileGroup
        fileSize
      }
      author {
        id
        firstName
        lastName
        avatar {
          key
        }
      }
      action
      createdAt
    }
  }
`

const loadMore = (result) => async () => {
  try {
    await result.fetchMore({
      variables: {
        offset: result.data.actions.length,
        limit: ACTIONS_LIMIT,
      },
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

const actionCreatedSubscriptionUpdateQuery = (prev, { subscriptionData }) => {
  if (!subscriptionData.data) return prev
  const { action: newItem } = subscriptionData.data.actionCreated
  const next = JSON.parse(JSON.stringify(prev))
  next.actions = [newItem, ...prev.actions]
  return next
}

const actionDeletedSubscriptionUpdateQuery = (prev, { subscriptionData }) => {
  if (!subscriptionData.data) return prev
  const next = JSON.parse(JSON.stringify(prev))
  next.actions = [...prev.actions.filter((action) => toId(action) !== toId(subscriptionData.data.actionDeleted))]
  return next
}

const actionCommentCreatedSubscriptionUpdateQuery = (prev, { subscriptionData }) => {
  if (!subscriptionData.data) return prev
  const newComment = subscriptionData.data.actionCommentCreated
  const next = JSON.parse(JSON.stringify(prev))
  const action = next.action
  if (action && !action.comments.find((c) => toId(c) === toId(newComment))) {
    action.comments.push(newComment)
  }
  return next
}

const subscribe = (result) => () =>
  [
    result.subscribeToMore({
      document: ACTION_CREATED_SUBSCRIPTION,
      updateQuery: actionCreatedSubscriptionUpdateQuery,
      onError: (err) => console.log("[useQueryActions][ACTION_CREATED_SUBSCRIPTION]", err),
    }),
    result.subscribeToMore({
      document: ACTION_UPDATED_SUBSCRIPTION,
      onError: (err) => console.log("[useQueryActions][ACTION_UPDATED_SUBSCRIPTION]", err),
    }),
    result.subscribeToMore({
      document: ACTION_DELETED_SUBSCRIPTION,
      updateQuery: actionDeletedSubscriptionUpdateQuery,
      onError: (err) => console.log("[useQueryActions][ACTION_DELETED_SUBSCRIPTION]", err),
    }),
    result.subscribeToMore({
      document: ACTION_COMMENT_CREATED_SUBSCRIPTION,
      updateQuery: actionCommentCreatedSubscriptionUpdateQuery,
      onError: (err) => console.log("[useQueryActions][ACTION_COMMENT_CREATED_SUBSCRIPTION]", err),
    }),
  ]

const useQueryActions = (options = {}) => {
  const { variables, ...otherOptions } = options
  const result = useQuery(ACTIONS_QUERY, {
    variables: {
      ...variables,
      offset: 0,
      limit: ACTIONS_LIMIT,
    },
    notifyOnNetworkStatusChange: true,
    ...otherOptions,
  })

  return {
    ...result,
    loadMore: loadMore(result),
    subscribe: subscribe(result),
  }
}

const useLazyQueryActions = () => {
  const [loadActions, result] = useLazyQuery(ACTIONS_QUERY, {
    variables: {
      offset: 0,
      limit: ACTIONS_LIMIT,
    },
    notifyOnNetworkStatusChange: true,
  })

  return [
    loadActions,
    {
      ...result,
      loadMore: loadMore(result),
      subscribe: subscribe(result),
    },
  ]
}

export {
  useQueryActions,
  useLazyQueryActions,
  ACTION_FIELDS,
  ACTIONS_QUERY,
  ACTION_CREATED_SUBSCRIPTION,
  ACTION_UPDATED_SUBSCRIPTION,
  ACTION_DELETED_SUBSCRIPTION,
  ACTION_COMMENT_CREATED_SUBSCRIPTION,
  actionsQueryTypePolicies,
  actionCreatedSubscriptionUpdateQuery,
  actionDeletedSubscriptionUpdateQuery,
  actionCommentCreatedSubscriptionUpdateQuery,
}
